"use client";

import { useEffect, useMemo } from "react";
import { Bot, Inbox, Maximize2, SquarePen, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUnreadChatCount } from "@stockline/web";
import { Button } from "@stockline/ui/components/button";
import { Label } from "@stockline/ui/components/label";
import { cn } from "@/lib/utils";
import { useTransportMode } from "@/hooks/use-transport-mode";
import { ChatSessionList } from "./ChatSessionList";
import { ChatWindow } from "./ChatWindow";
import { useDockedChat } from "./useDockedChat";

interface SidePanelChatViewProps {
  /** Close the floating card. */
  onClose: () => void;
  /** Show the "expand to the full /ai page" link in the header. */
  showExpand?: boolean;
  /**
   * Whether the floating card is open. The card stays mounted while closed, so
   * this lets the view reset its transient sub-state (the history list) on
   * close instead of preserving it across reopens.
   */
  open?: boolean;
}

/**
 * SidePanelChatView — chat surface for the floating AI card.
 *
 * Opens on a blank composer; the thread last opened in this tab comes back after
 * a reload (see `useDockedChat`). A dedicated History toggle in the merged
 * header reveals the list of past conversations (pressed state returns to the
 * conversation), a New-chat action starts a blank one, and selecting a row
 * opens it. Owns the card's header actions (new chat, history, expand, close)
 * so there is one header, not two. Reuses ChatSessionList (compact) +
 * ChatWindow.
 */
export function SidePanelChatView({
  onClose,
  showExpand = true,
  open = true,
}: Readonly<SidePanelChatViewProps>) {
  const { t } = useTranslation();
  const isTransportMode = useTransportMode();
  // Own `surface` slot: the steering dock is mounted at the same time and would
  // otherwise share this tab's remembered thread.
  const {
    selectedSessionId,
    showList,
    setShowList,
    startNewChat,
    selectSession,
  } = useDockedChat("assistant");
  const unreadInboxCount = useUnreadChatCount();

  // The card persists while closed, so a history view left open would still be
  // showing on the next open — and the card's open-focus logic, which targets
  // the composer, would find nothing to focus. Drop back to the conversation
  // on close.
  useEffect(() => {
    if (!open) setShowList(false);
  }, [open, setShowList]);

  const historyLabel = showList
    ? t("common.sidePanelChat.backToConversation", "Back to conversation")
    : t("common.sidePanelChat.pastConversations", "Past conversations");

  // Transport tenants track positions and senders/recipients rather than
  // sales/stock, so the empty-chat starters swap to transport vocabulary.
  // Memoized (matching ChatTab) so ChatWindow gets a stable array reference.
  const emptyChatSuggestions = useMemo(() => {
    const base = isTransportMode
      ? "common.aiManagement.emptyChatTransport"
      : "common.aiManagement.emptyChat";
    return [
      t(`${base}.suggestion1`),
      t(`${base}.suggestion2`),
      t(`${base}.suggestion3`),
      t(`${base}.suggestion4`),
    ];
  }, [t, isTransportMode]);

  return (
    <div className="flex flex-col h-full">
      {/* Single merged header. Left padding clears the corner resize grip.
          The two primary actions (new chat, history) sit on the right next to
          expand/close and carry text labels so they read at a glance. The
          header is a query container: when it gets too narrow (card resized
          down), the labels collapse to icon-only. */}
      <div className="flex items-center gap-1 border-b bg-muted/30 py-2 pl-7 pr-2 [container-type:inline-size]">
        <Bot className="h-4 w-4 text-violet-500 flex-shrink-0" />
        <Label className="flex-1 truncate text-sm font-medium">
          {t("common.sidePanelChat.headerTitle", "Speak with your AI")}
        </Label>
        {/* New chat — blank composer (no session created until first send). */}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 px-2 text-xs font-medium flex-shrink-0"
          onClick={startNewChat}
          title={t("common.sidePanelChat.newChatButton", "New chat")}
          aria-label={t("common.sidePanelChat.newChatButton", "New chat")}
          data-testid="ai-assistant-new-chat"
        >
          <SquarePen className="h-4 w-4" />
          <span className="hidden [@container_(min-width:26rem)]:inline">
            {t("common.sidePanelChat.newChatButton", "New chat")}
          </span>
        </Button>
        {/* History toggle — opens the past-conversations list; its pressed
            state returns to the conversation. */}
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-7 gap-1.5 px-2 text-xs font-medium flex-shrink-0",
            showList && "bg-violet-100 text-violet-700 hover:bg-violet-100",
          )}
          aria-pressed={showList}
          onClick={() => setShowList((v) => !v)}
          title={historyLabel}
          aria-label={historyLabel}
          data-testid="ai-assistant-history"
        >
          <Inbox className="h-4 w-4" />
          <span className="hidden [@container_(min-width:26rem)]:inline">
            {t("common.aiManagement.inboxTab", "Inbox")}
          </span>
          {unreadInboxCount > 0 && (
            <span
              className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-violet-500 px-1 text-[10px] font-semibold leading-none text-white"
              data-testid="ai-assistant-inbox-unread"
              aria-label={t("common.aiManagement.unreadBadge", {
                count: unreadInboxCount,
              })}
            >
              {unreadInboxCount > 99 ? "99+" : unreadInboxCount}
            </span>
          )}
        </Button>
        {showExpand && (
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0"
            title={t("common.aiManagement.openFullPage", "Open full page")}
          >
            <Link
              to="/ai"
              onClick={onClose}
              data-testid="ai-assistant-expand"
              aria-label={t(
                "common.aiManagement.openFullPage",
                "Open full page",
              )}
            >
              <Maximize2 className="h-4 w-4" />
            </Link>
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 flex-shrink-0"
          onClick={onClose}
          data-testid="ai-assistant-close"
          title={t("common.close", "Close")}
          aria-label={t("common.close", "Close")}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Body: past conversations, or the active/new conversation. */}
      <div className="min-h-0 flex-1">
        {showList ? (
          <ChatSessionList
            selectedId={selectedSessionId}
            onSelect={selectSession}
            mode="unified"
            compact
            showNotifications
            excludeBi
          />
        ) : (
          <ChatWindow
            sessionId={selectedSessionId}
            // Pin the freshly-created session so the rest of the turn (and any
            // follow-up) lands on it rather than on another blank composer.
            onSessionCreated={selectSession}
            mode="unified"
            emptySuggestionsHeading={t("common.aiManagement.emptyChat.heading")}
            emptySuggestions={emptyChatSuggestions}
          />
        )}
      </div>
    </div>
  );
}
